import { useEffect, useState } from "react";
import api, { STATUS_META, STATUS_CODES } from "../lib/api";
import Layout from "../components/Layout";
import TimesheetCalendar from "../components/TimesheetCalendar";
import ConversationSheet from "../components/ConversationSheet";
import { StatusPill, MonthlySummary, Legend } from "./EmployeeDashboard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Download, Eye, Trash2, Loader2, UserPlus, Unlock } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

export function AdminApprovals() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("submitted");
  const nav = useNavigate();

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/timesheets${q}`).then(r => setItems(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto fade-up">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— Admin</p>
        <h1 className="font-display text-4xl tracking-tight font-light mb-8">Approvals</h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Label className="text-xs text-muted-foreground">Filter:</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-background border-border" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="clarification">Clarification</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="approvals-table">
            <thead className="bg-muted/40 text-muted-foreground text-[11px] uppercase tracking-[0.18em] font-mono">
              <tr>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-left px-4 py-3">Working Days</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-foreground">{t.user?.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{t.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-foreground/90">{t.year}-{String(t.month).padStart(2,"0")}</td>
                  <td className="px-4 py-3 font-display text-blue-600 dark:text-blue-400">{t.summary?.working_days || 0}</td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => nav(`/admin/review/${t.user_id}/${t.year}/${t.month}`)} data-testid={`review-${t.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> Review
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No timesheets found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", employee_type: "employee" });
  const [busy, setBusy] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "employee", employee_type: "employee" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirmUser) return;
    setBusy(true);
    try {
      await api.delete(`/users/${confirmUser.id}`);
      toast.success(`Deleted ${confirmUser.email}`);
      setConfirmUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[1200px] mx-auto fade-up">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— Admin</p>
            <h1 className="font-display text-4xl tracking-tight font-light">Users</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="open-create-user"><UserPlus className="h-4 w-4 mr-1.5" /> New User</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground">
              <DialogHeader><DialogTitle className="font-display">Create User</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Full name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
                <div><Label>Email (must end with @dataeko.ai)</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
                <div><Label>Password (optional — leave blank for Google-only sign-in)</Label><Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Leave empty for Google-only" data-testid="new-user-password" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="accounts">Accounts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.employee_type} onValueChange={(v) => setForm({ ...form, employee_type: v })}>
                      <SelectTrigger data-testid="new-user-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="intern">Intern</SelectItem>
                        <SelectItem value="consultant">Consultant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy} className="bg-blue-500 hover:bg-blue-400 text-white" data-testid="create-user-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-muted/40 text-muted-foreground text-[11px] uppercase tracking-[0.18em] font-mono">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-foreground/90">{u.email}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded font-mono bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/30 uppercase tracking-wider">{u.role}</span></td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{u.employee_type}</td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== me?.id && <Button variant="ghost" size="sm" onClick={() => setConfirmUser(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>}
                    {u.id === me?.id && <span className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest">you</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!confirmUser} onOpenChange={(v) => !v && setConfirmUser(null)}>
        <AlertDialogContent className="bg-card border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete user?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {confirmUser && <>This will permanently delete <span className="font-mono text-foreground">{confirmUser.email}</span> ({confirmUser.role}) and they will lose access immediately. This cannot be undone.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-border text-foreground/90 hover:bg-accent/70" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-red-500 hover:bg-red-400 text-white" data-testid="delete-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

export function ReviewTimesheet() {
  const { userId, year, month } = useParams();
  const [ts, setTs] = useState(null);
  const [convoOpen, setConvoOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockMsg, setUnlockMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/timesheet/${userId}/${year}/${month}`).then(r => setTs(r.data));
  useEffect(() => { load(); }, [userId, year, month]);

  const downloadOne = async () => {
    const token = localStorage.getItem("dataeko_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/timesheet/${ts.id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${ts.id}.xlsx`; a.click();
  };

  const doUnlock = async () => {
    setBusy(true);
    try {
      await api.post("/timesheet/unlock", { timesheet_id: ts.id, message: unlockMsg.trim() });
      toast.success("Unlocked — employee can now edit and resubmit");
      setUnlockOpen(false);
      setUnlockMsg("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto fade-up">
        {ts && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— Reviewing</p>
                <h1 className="font-display text-4xl tracking-tight font-light">{ts.user?.name}</h1>
                <p className="text-muted-foreground font-mono text-sm">{ts.user?.email} · {year}-{String(month).padStart(2,"0")}</p>
              </div>
              <div className="flex gap-2">
                <StatusPill status={ts.status} />
                <Button variant="outline" onClick={downloadOne} data-testid="download-one"><Download className="h-4 w-4 mr-1.5" /> Excel</Button>
                {(ts.status === "submitted" || ts.status === "approved" || ts.status === "clarification") && (
                  <Button variant="outline" onClick={() => setUnlockOpen(true)} data-testid="open-unlock" className="border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10">
                    <Unlock className="h-4 w-4 mr-1.5" /> Unlock
                  </Button>
                )}
                <Button onClick={() => setConvoOpen(true)} className="bg-blue-500 hover:bg-blue-400 text-white" data-testid="open-review-convo">Open Conversation</Button>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
              <TimesheetCalendar year={Number(year)} month={Number(month)} entries={ts.entries} onChange={() => {}} locked />
              <aside className="space-y-4">
                <MonthlySummary summary={ts.summary} />
                <Legend />
              </aside>
            </div>
            <ConversationSheet open={convoOpen} onOpenChange={setConvoOpen} ts={ts} onUpdated={load} role="admin" />

            <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
              <DialogContent className="bg-card border-border text-foreground">
                <DialogHeader>
                  <DialogTitle className="font-display">Unlock timesheet</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  This will revert the timesheet to <span className="font-mono">draft</span> so {ts.user?.name} can edit and resubmit. An email + in-app notification is sent to them (and to accounts).
                </p>
                <Input
                  value={unlockMsg}
                  onChange={e => setUnlockMsg(e.target.value)}
                  placeholder="Optional message (e.g., 'Please correct Apr 15 and resubmit')"
                  data-testid="unlock-message"
                />
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setUnlockOpen(false)} data-testid="unlock-cancel">Cancel</Button>
                  <Button onClick={doUnlock} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="unlock-confirm">
                    {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Unlock
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </Layout>
  );
}
